import { Card, CardContent, CardHeader, CardTitle, LoadingCard } from '@folio/components';
import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import { formatDate } from '@folio/lib/date-formatting';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import { claimManagementControllerGetQueryOptions } from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { claimSectionItems } from './formatClaimFieldValue';
import { type InformationItem, InformationList } from './InformationList';

const INCIDENT_SECTION = 'incident';

export function ClaimInformationModule() {
  const { t, i18n } = useTranslation();
  const { claimId } = useAssertedParams<{ claimId: string }>();

  const {
    data: claim,
    isLoading,
    isError,
  } = useQuery(claimManagementControllerGetQueryOptions(claimId));

  if (isLoading) {
    return <LoadingCard />;
  }

  if (isError || !claim) {
    return <ErrorMessage>{t('claimInformation.error')}</ErrorMessage>;
  }

  const toDate = (iso?: string) => (iso ? formatDate(new Date(iso), i18n.language) : null);
  const booleanLabels = { yes: t('options.true'), no: t('options.false') };

  const generalItems: InformationItem[] = [
    {
      id: 'type',
      label: t('claimInformation.type'),
      value: claim.claimTypeLabel?.join(' > ') || claim.claimType,
    },
    {
      id: 'occurredAt',
      label: t('claimInformation.occurredAt'),
      value: toDate(claim.incident.occurredAt),
    },
    { id: 'policy', label: t('claimInformation.policy'), value: claim.policyId },
    { id: 'declaredAt', label: t('claimInformation.declaredAt'), value: toDate(claim.submittedAt) },
    ...(claim.externalClaimId
      ? [
          {
            id: 'externalReference',
            label: t('claimInformation.externalReference'),
            value: claim.externalClaimId,
          },
        ]
      : []),
    ...(claim.discardedAt
      ? [
          {
            id: 'discardedAt',
            label: t('claimInformation.discardedAt'),
            value: toDate(claim.discardedAt),
          },
          {
            id: 'discardReason',
            label: t('claimInformation.discardReason'),
            value: claim.discardReason,
          },
        ]
      : []),
  ];

  const incidentFieldItems = claimSectionItems(claim.fields, INCIDENT_SECTION, {
    locale: i18n.language,
    booleanLabels,
  });

  const incidentItems: InformationItem[] = [
    { id: 'location', label: t('claimInformation.location'), value: claim.incident.location },
    {
      id: 'circumstances',
      label: t('claimInformation.circumstances'),
      value: claim.incident.circumstances,
      fullWidth: true,
    },
    {
      id: 'description',
      label: t('claimInformation.description'),
      value: claim.incident.description,
      fullWidth: true,
    },
    ...incidentFieldItems,
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('claimInformation.general')}</CardTitle>
        </CardHeader>
        <CardContent>
          <InformationList items={generalItems} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('claimInformation.incident')}</CardTitle>
        </CardHeader>
        <CardContent>
          <InformationList items={incidentItems} />
        </CardContent>
      </Card>
    </div>
  );
}
